import {
  getAuthApiUrl,
  getStoredSessionToken,
  isSessionExpiredPayload,
  isSessionRefreshRequiredPayload,
  shouldDebugControlPlaneSession,
} from '../../../app/services/session.service';

const WORKSPACE_ACCESS_API_PATH = '/api/admin/workspace-access';

function buildResult({ ok, data = null, error = null, message = '' }) {
  return {
    ok,
    data,
    error,
    message,
  };
}

function getApiUrl(path = WORKSPACE_ACCESS_API_PATH) {
  return getAuthApiUrl(path);
}

function getAuthHeaders(extraHeaders = {}) {
  const token = getStoredSessionToken();

  if (!token) {
    return extraHeaders;
  }

  return {
    ...extraHeaders,
    Authorization: `Bearer ${token}`,
  };
}

async function parseJson(response) {
  try {
    return await response.json();
  } catch {
    return null;
  }
}

function debugWorkspaceAccess(event, details = {}) {
  if (!shouldDebugControlPlaneSession()) {
    return;
  }

  // Controlled debug for dev/prealpha only.
  console.info('[control-plane][workspace-access]', event, details);
}

export async function createWorkspaceAccess(tenantId) {
  try {
    const token = getStoredSessionToken();
    const response = await fetch(getApiUrl(), {
      method: 'POST',
      headers: getAuthHeaders({
        'Content-Type': 'application/json',
      }),
      body: JSON.stringify({ tenantId }),
    });

    const payload = await parseJson(response);
    debugWorkspaceAccess('create-response', {
      tenantId,
      status: response.status,
      ok: response.ok,
      backendError: payload?.error || '',
      hasToken: Boolean(token),
    });

    if (response.status === 401 && isSessionRefreshRequiredPayload(payload)) {
      return buildResult({
        ok: false,
        error: 'WORKSPACE_ACCESS_SESSION_REFRESH_REQUIRED',
        message: payload?.message || 'Sessione non aggiornata. Effettua di nuovo l\'accesso.',
      });
    }

    if (response.status === 401 && isSessionExpiredPayload(payload)) {
      return buildResult({
        ok: false,
        error: 'WORKSPACE_ACCESS_SESSION_EXPIRED',
        message: payload?.message || 'Sessione scaduta. Effettua di nuovo il login.',
      });
    }

    if (response.status === 403) {
      return buildResult({
        ok: false,
        error:
          payload?.error === 'workspace_access_not_allowed'
            ? 'WORKSPACE_ACCESS_WORKSPACE_SESSION_BLOCKED'
            : payload?.error === 'platform_admin_required'
              ? 'WORKSPACE_ACCESS_PLATFORM_ADMIN_REQUIRED'
              : 'WORKSPACE_ACCESS_FORBIDDEN',
        message: payload?.message || 'Accesso al workspace non consentito per questa sessione.',
      });
    }

    if (response.status === 400) {
      return buildResult({
        ok: false,
        error: 'WORKSPACE_ACCESS_INVALID',
        message: 'Workspace target non valido per accesso amministrativo.',
      });
    }

    if (response.status === 404) {
      return buildResult({
        ok: false,
        error: 'WORKSPACE_ACCESS_NOT_FOUND',
        message: 'Tenant target non trovato.',
      });
    }

    if (!response.ok) {
      throw new Error(`WORKSPACE_ACCESS_FAILED_${response.status}`);
    }

    return buildResult({
      ok: true,
      data: {
        token: payload?.token || '',
        expiresAt: payload?.expiresAt || null,
        tenantId: payload?.tenantId || '',
        tenant: payload?.tenant || null,
        workspaceAccess: payload?.workspaceAccess === true,
        role: payload?.role || '',
      },
    });
  } catch {
    return buildResult({
      ok: false,
      error: 'WORKSPACE_ACCESS_UNAVAILABLE',
      message: 'Accesso workspace non disponibile.',
    });
  }
}

export async function exitWorkspaceAccess() {
  try {
    const response = await fetch(getApiUrl(`${WORKSPACE_ACCESS_API_PATH}/exit`), {
      method: 'POST',
      headers: getAuthHeaders(),
    });

    const payload = await parseJson(response);
    debugWorkspaceAccess('exit-response', {
      status: response.status,
      ok: response.ok,
      backendError: payload?.error || '',
    });

    if (response.status === 401 && isSessionRefreshRequiredPayload(payload)) {
      return buildResult({
        ok: false,
        error: 'WORKSPACE_ACCESS_SESSION_REFRESH_REQUIRED',
        message: payload?.message || 'Sessione non aggiornata. Effettua di nuovo l\'accesso.',
      });
    }

    if (response.status === 401 && isSessionExpiredPayload(payload)) {
      return buildResult({
        ok: false,
        error: 'WORKSPACE_ACCESS_SESSION_EXPIRED',
        message: payload?.message || 'Sessione scaduta. Effettua di nuovo il login.',
      });
    }

    if (response.status === 403) {
      return buildResult({
        ok: false,
        error:
          payload?.error === 'workspace_access_session_required'
            ? 'WORKSPACE_ACCESS_EXIT_SESSION_REQUIRED'
            : payload?.error === 'platform_admin_required'
              ? 'WORKSPACE_ACCESS_PLATFORM_ADMIN_REQUIRED'
              : 'WORKSPACE_ACCESS_EXIT_FORBIDDEN',
        message: payload?.message || 'Chiusura workspace access non consentita per questa sessione.',
      });
    }

    if (!response.ok) {
      return buildResult({
        ok: false,
        error: 'WORKSPACE_ACCESS_EXIT_FAILED',
        message: 'Chiusura workspace access non confermata dal backend.',
      });
    }

    return buildResult({
      ok: true,
      data: {
        token: payload?.token || '',
        expiresAt: payload?.expiresAt || null,
        user: payload?.user || null,
      },
    });
  } catch {
    return buildResult({
      ok: false,
      error: 'WORKSPACE_ACCESS_EXIT_UNAVAILABLE',
      message: 'Chiusura workspace access non disponibile.',
    });
  }
}
